'use client'

import React, { createContext, useContext, useCallback, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { usePathname, useRouter } from 'next/navigation'
import { useUser } from '@auth0/nextjs-auth0/client'
import { ICompany, ICompanyConfig, ProviderProps } from '../types/types'
import { useToast } from './toast-provider'
import { useUserContext } from './UserContext'
import axios, { AxiosResponse } from 'axios'
import { UserRole } from '@/constants/enums'
import { getAppRolesFromUser } from '@/utils/app-roles.util'
import { isPublicPageRoute } from '@/utils/isPublicPageRoute.util'

export const dynamic = 'force-dynamic'

const COMPANY_QUERY_KEY = 'company'

// Create a context to manage Company-related data and functions
const CompanyContext = createContext({
    error: '',
    company: {} as ICompany,
    companyConfig: {} as ICompanyConfig,
    getCompanyDetails: async (data: ICompany, setIsLoading: (loading: boolean) => void) => {},
    isCompanyAccessDisabled: false,
    isCompanyReadonly: false,
    /** True once the company + config query has succeeded — flags read as "off" before that. */
    isCompanyLoaded: false,
})

type CompanyData = ICompany & { companyConfig?: ICompanyConfig }

// Create a hook to access the CompanyContext
const useCompanyContext = () => useContext(CompanyContext)

// Create a component
const CompanyProvider: React.FC<ProviderProps> = ({ children }) => {
    const { showErrorToast } = useToast()
    const router = useRouter()
    const pathname = usePathname()
    const { user: auth0User, isLoading: isAuth0Loading } = useUser()
    const { user: contextUser, isUserLoading } = useUserContext()

    const companyId = (auth0User as any)?.companyId ?? (contextUser as any)?.companyId
    const queryKey = [COMPANY_QUERY_KEY, companyId]

    const {
        data,
        error: queryError,
        refetch,
        isSuccess: isCompanyQuerySuccess,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            const res = await axios.get('/api/company', { params: { requestType: 'orgId' } })
            return res.data as AxiosResponse<CompanyData>
        },
        enabled: Boolean(companyId) && !isAuth0Loading && !isUserLoading,
        throwOnError: false,
    })

    const companyData = data?.data as (ICompany & { companyConfig?: ICompanyConfig }) | undefined
    const company = companyData ?? ({} as ICompany)
    const companyConfig = companyData?.companyConfig ?? ({} as ICompanyConfig)
    const error = typeof queryError === 'string' ? queryError : (queryError?.message ?? '')

    const getCompanyDetails = useCallback(
        async (params: ICompany, setIsLoading: (loading: boolean) => void) => {
            if (!companyId) return
            if (company && Object.keys(company).length > 0) return
            setIsLoading(true)
            try {
                await refetch()
            } finally {
                setIsLoading(false)
            }
        },
        [company, companyId, refetch]
    )

    const roles = useMemo(() => {
        return getAppRolesFromUser(auth0User as Record<string, unknown> | null | undefined)
    }, [auth0User])

    const isCompanyAccessDisabled = useMemo(() => {
        if (!auth0User) return false
        if (!companyId) return false
        if (!isCompanyQuerySuccess) return false
        return companyConfig.isAccessDisabled === true
    }, [auth0User, companyId, isCompanyQuerySuccess, pathname, companyConfig.isAccessDisabled])

    const isCompanyReadonly = useMemo(() => {
        if (!auth0User) return false
        if (!companyId) return false
        if (!isCompanyQuerySuccess) return false
        return companyConfig.isCompanyReadonly === true
    }, [auth0User, companyId, isCompanyQuerySuccess, pathname, companyConfig.isCompanyReadonly])

    useEffect(() => {
        if (
            company?.companySlug &&
            !pathname.includes(company.companySlug) &&
            !isPublicPageRoute(pathname) &&
            roles.length > 0 &&
            !roles.includes(UserRole.SuperAdmin)
        ) {
            router.push(`/${company.companySlug}`)
        }
    }, [company?.companySlug, pathname, roles, router])

    useEffect(() => {
        if (queryError) {
            showErrorToast(typeof queryError === 'string' ? queryError : (queryError?.message ?? ''))
        }
    }, [queryError, showErrorToast])

    return (
        <CompanyContext.Provider
            value={{
                error,
                company,
                companyConfig,
                getCompanyDetails,
                isCompanyAccessDisabled,
                isCompanyReadonly,
                isCompanyLoaded: isCompanyQuerySuccess,
            }}
        >
            {children}
        </CompanyContext.Provider>
    )
}

// Export the context, provider component, and custom hook
export { CompanyContext, CompanyProvider, useCompanyContext }
